import {
  LayoutDashboard,
  Flame,
  Map as MapIcon,
  BarChart3,
  Sun,
  Users,
  Shield,
  Inbox,
  Settings as SettingsIcon,
  User as UserIcon,
  Building2,
  Mail,
  Droplets,
  FileText,
  Images,
  type LucideIcon,
} from 'lucide-react';

/** Capability a nav item requires; omitted = always visible to any authed user. */
export interface NavRequire {
  resource: string;
  action: string;
}

export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** Match by prefix instead of exact path. */
  matchPrefix?: boolean;
  /** Capability gate — the item is hidden unless `can(resource, action)`. */
  requires?: NavRequire;
}

export interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

export interface NavContext {
  /** Capability check from AuthContext: can(resource, action). */
  can: (resource: string, action: string) => boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isFPAAdmin: boolean;
}

/**
 * Build the nav, hiding any item the user lacks permission for. Items declare
 * the capability they need via `requires`; the admin console grouping stays
 * role-based (those pages span many permissions). Groups left empty after
 * filtering are dropped.
 */
export function getNavGroups({ can, isAdmin, isSuperAdmin, isFPAAdmin }: NavContext): NavGroup[] {
  const groups: NavGroup[] = [
    {
      id: 'overview',
      label: 'Overview',
      items: [{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard }],
    },
    {
      id: 'wildfires',
      label: 'Wildfires',
      items: [
        { to: '/wildfires', label: 'All wildfires', icon: Flame, matchPrefix: true, requires: { resource: 'wildfire', action: 'list' } },
        { to: '/wildfires/new', label: 'Report new', icon: Flame, requires: { resource: 'wildfire', action: 'create' } },
        { to: '/permit-burns', label: 'Permit burns', icon: FileText, matchPrefix: true, requires: { resource: 'wildfire', action: 'list' } },
        { to: '/stats', label: 'Statistics', icon: BarChart3, requires: { resource: 'reporting', action: 'read' } },
        { to: '/forecast', label: 'Forecast', icon: Sun, requires: { resource: 'weather', action: 'read' } },
      ],
    },
    {
      // Per-FPA self-service area. Each item is gated by its own capability so a
      // view-only role (FPA_USER) sees what it can read but not management pages.
      id: 'fpa',
      label: 'My FPA',
      items: [
        { to: '/fpa-settings', label: 'FPA settings', icon: Building2, matchPrefix: true, requires: { resource: 'tenant', action: 'update' } },
        { to: '/members', label: 'Members', icon: Users, matchPrefix: true, requires: { resource: 'fpa_member', action: 'list' } },
        { to: '/firefighters', label: 'Firefighters', icon: Shield, matchPrefix: true, requires: { resource: 'firefighter', action: 'list' } },
        { to: '/water-sources', label: 'Water sources', icon: Droplets, matchPrefix: true, requires: { resource: 'water_source', action: 'list' } },
        { to: '/fpa-gallery', label: 'Gallery', icon: Images, matchPrefix: true, requires: { resource: 'image', action: 'list' } },
      ],
    },
    {
      id: 'inbox',
      label: 'Inbox',
      items: [
        { to: '/inbox', label: 'Pending content', icon: Inbox, matchPrefix: true, requires: { resource: 'image', action: 'moderate' } },
      ],
    },
  ];

  // Admin console — role-based grouping (these pages span many permissions).
  if (isAdmin || isSuperAdmin) {
    groups.push({
      id: 'admin',
      label: 'Admin',
      items: [
        { to: '/admin', label: 'Global admin', icon: Building2, matchPrefix: true },
        { to: '/admin/invites', label: 'Invitations', icon: Mail },
        { to: '/members/parcels', label: 'Parcel pipeline', icon: MapIcon },
      ],
    });
  } else if (isFPAAdmin) {
    // FPA admins manage their own FPA's members (invite + promote/demote).
    groups.push({
      id: 'fpa-admin',
      label: 'FPA Admin',
      items: [
        { to: '/admin?tab=users', label: 'Users', icon: Users, matchPrefix: true },
        { to: '/admin/invites', label: 'Invitations', icon: Mail },
      ],
    });
  }

  groups.push({
    id: 'profile',
    label: 'Account',
    items: [
      { to: '/profile', label: 'My profile', icon: UserIcon },
      ...(isAdmin || isSuperAdmin
        ? [{ to: '/admin?tab=system', label: 'System settings', icon: SettingsIcon }]
        : []),
    ],
  });

  // Hide items the user lacks the capability for, then drop empty groups.
  return groups
    .map((g) => ({
      ...g,
      items: g.items.filter((it) => !it.requires || can(it.requires.resource, it.requires.action)),
    }))
    .filter((g) => g.items.length > 0);
}
